import {
  DEFAULT_DRAFT_HTML,
  IDLE_THRESHOLD_MS,
  MAJOR_GROWTH_CHAR_DELTA,
  MAX_EVENT_LOG,
  MAX_MAJOR_GROWTH_EVENTS,
  REVISION_BURST_THRESHOLD,
  REVISION_WINDOW_MS,
  SESSION_TICK_MS,
  countWords,
  createDraft,
  htmlToStructuredText,
} from "@context/shared";
import type { Draft, Submission, Workspace, WritingEvent, WritingEventType } from "@context/shared";
import { draftHtmlFromStructuredText } from "../lib/sanitizeHtml";
import { loadSessions } from "./draftSessions";
import type { DraftSession } from "./draftSessions";

/** Sources of a change to the editor content, used for activity bookkeeping. */
export type MutationSource = "input" | "formatting";

/**
 * Defensive ceiling on the unsent event buffer. If the server is unreachable
 * for a long offline session the buffer would otherwise grow without bound;
 * past this cap the oldest events drop off first, keeping the newest.
 */
export const MAX_PENDING_EVENTS = 1000;

export interface StudentState {
  status: "loading" | "ready" | "error";
  error: string | null;
  referencePrompt: string;
  draft: Draft;
  /** Text of the "Save status:" chip. */
  saveStatus: string;
  /** Transient guidance line shown under the assignment prompt. */
  guidance: string | null;
  /** Bumped whenever the editor DOM must be rewritten imperatively. */
  contentVersion: number;
  /** Events recorded locally but not yet flushed to the service. */
  pendingEvents: WritingEvent[];
  /** Draft sessions saved in this browser, newest first. */
  sessions: DraftSession[];
  /** Id of the saved session currently loaded into the editor, if any. */
  selectedSessionId: string | null;
}

export type StudentAction =
  | { type: "loading" }
  | { type: "loaded"; workspace: Workspace }
  | { type: "error"; message: string }
  | { type: "setProfessorEmail"; value: string }
  | { type: "setStudentEmail"; value: string }
  | { type: "setReferencePrompt"; value: string }
  | { type: "sessionStarted"; draft: Draft }
  | { type: "mutation"; html: string; text: string; source: MutationSource; now: string; paragraphCount: number }
  | { type: "keydown"; key: string; now: string }
  | { type: "paste"; characters: number; words: number; now: string }
  | { type: "editorFocus"; now: string }
  | { type: "editorBlur"; now: string }
  | { type: "tick"; now: number }
  | { type: "sessionsUpdated"; sessions: DraftSession[] }
  | { type: "sessionSelected"; id: string }
  | { type: "sessionLoaded"; session: DraftSession }
  | { type: "draftCleared" }
  | { type: "saveStatus"; value: string }
  | { type: "submitted"; submission: Submission; draft: Draft }
  | { type: "guidance"; message: string | null }
  | { type: "eventsFlushed"; sent: WritingEvent[] }
  | { type: "eventsQueued"; events: WritingEvent[] };

export function createInitialState(): StudentState {
  return {
    status: "loading",
    error: null,
    referencePrompt: "",
    draft: createDraft(DEFAULT_DRAFT_HTML),
    saveStatus: "Not saved",
    guidance: null,
    contentVersion: 0,
    pendingEvents: [],
    sessions: loadSessions(),
    selectedSessionId: null,
  };
}

function logEvent(
  draft: Draft,
  pending: WritingEvent[],
  type: WritingEventType,
  timestamp: string,
  payload: WritingEvent["payload"] = {},
): { events: WritingEvent[]; pendingEvents: WritingEvent[] } {
  const event: WritingEvent = { type, timestamp, payload };
  return {
    events: [...draft.events, event].slice(-MAX_EVENT_LOG),
    // The buffer is capped defensively so a long offline stretch cannot grow
    // memory without bound; the newest events are kept, the oldest dropped.
    pendingEvents: [...pending, event].slice(-MAX_PENDING_EVENTS),
  };
}

/** Records that the student did something, for the idle/active split. */
function markActivity(draft: Draft, now: string, isInput: boolean): Draft["activity"] {
  return {
    ...draft.activity,
    lastActivityAt: now,
    lastInputAt: isInput ? now : draft.activity.lastInputAt,
  };
}

/**
 * The prototype counted one revision burst per rolling 15-second window in
 * which six or more changes landed. Reproduced exactly.
 */
function applyRevisionBurst(
  draft: Draft,
  now: string,
): { activity: Draft["activity"]; burstRecorded: boolean } {
  const nowMs = new Date(now).getTime();
  const windowStart = draft.activity.revisionWindowStart
    ? new Date(draft.activity.revisionWindowStart).getTime()
    : 0;

  if (!windowStart || nowMs - windowStart > REVISION_WINDOW_MS) {
    return {
      activity: { ...draft.activity, revisionWindowStart: now, revisionWindowChanges: 1 },
      burstRecorded: false,
    };
  }

  const revisionWindowChanges = draft.activity.revisionWindowChanges + 1;
  return {
    activity: { ...draft.activity, revisionWindowChanges },
    burstRecorded: revisionWindowChanges === REVISION_BURST_THRESHOLD,
  };
}

// eslint-disable-next-line complexity
export function studentReducer(state: StudentState, action: StudentAction): StudentState {
  switch (action.type) {
    case "loading":
      return { ...state, status: "loading", error: null };

    case "loaded": {
      // A reloaded page always starts as a brand-new draft: the server's copy
      // of the text, the metrics and the prompt are deliberately ignored so a
      // refresh never repopulates the editor. Only the identity fields (the
      // emails) survive; content returns when the student selects a session.
      const fresh = createDraft("");
      return {
        ...state,
        status: "ready",
        error: null,
        referencePrompt: "",
        draft: {
          ...fresh,
          professorEmail: action.workspace.currentDraft.professorEmail,
          studentEmail: action.workspace.currentDraft.studentEmail,
          trackingActive: false,
          activity: { ...fresh.activity, isFocused: false },
        },
        saveStatus: "Not saved",
        contentVersion: state.contentVersion + 1,
      };
    }

    case "error":
      return { ...state, status: "error", error: action.message };

    case "setProfessorEmail":
      return { ...state, draft: { ...state.draft, professorEmail: action.value } };

    case "setStudentEmail":
      return { ...state, draft: { ...state.draft, studentEmail: action.value } };

    case "setReferencePrompt":
      return { ...state, referencePrompt: action.value };

    case "sessionStarted":
      return {
        ...state,
        draft: action.draft,
        guidance: null,
        selectedSessionId: null,
        // If the API ever returns a differently-shaped draft, the editor DOM
        // must be rewritten from it, exactly like every other draft-replacing
        // action.
        contentVersion: state.contentVersion + 1,
      };

    case "mutation": {
      if (!state.draft.trackingActive) return state;

      const draft = state.draft;
      const previousLength = draft.text.length;
      const nextLength = action.text.length;
      const delta = nextLength - previousLength;

      let events = draft.events;
      let pendingEvents = state.pendingEvents;

      let next: Draft = {
        ...draft,
        html: action.html,
        text: action.text,
        metrics: {
          ...draft.metrics,
          totalCharacters: nextLength,
          wordCount: countWords(action.text),
        },
      };

      if (delta >= MAJOR_GROWTH_CHAR_DELTA) {
        const growthEvent = {
          timestamp: action.now,
          charsAdded: delta,
          paragraphIndex: Math.max(0, action.paragraphCount - 1),
        };
        next.metrics = {
          ...next.metrics,
          // The API schema rejects drafts whose majorGrowthEvents exceeds
          // MAX_MAJOR_GROWTH_EVENTS, so the local list must never pass the cap
          // or every subsequent PUT /drafts/current would 400. Mirror the
          // event-log cap (appendEvent in the service): keep the newest 500.
          majorGrowthEvents: [...next.metrics.majorGrowthEvents, growthEvent].slice(
            -MAX_MAJOR_GROWTH_EVENTS,
          ),
        };
        ({ events, pendingEvents } = logEvent(
          { ...next, events },
          pendingEvents,
          "major_growth",
          action.now,
          growthEvent,
        ));
      }

      if (action.source === "input") {
        ({ events, pendingEvents } = logEvent(
          { ...next, events },
          pendingEvents,
          "text_input",
          action.now,
          {
            charsDelta: delta,
            wordCount: next.metrics.wordCount,
            totalCharacters: nextLength,
          },
        ));
      }

      if (delta !== 0) {
        const burst = applyRevisionBurst(next, action.now);
        next = { ...next, activity: burst.activity };
        if (burst.burstRecorded) {
          next.metrics = { ...next.metrics, revisionBursts: next.metrics.revisionBursts + 1 };
          ({ events, pendingEvents } = logEvent(
            { ...next, events },
            pendingEvents,
            "revision_burst",
            action.now,
            { changes: next.activity.revisionWindowChanges },
          ));
        }
      }

      next.activity = markActivity(next, action.now, true);

      return { ...state, draft: { ...next, events }, pendingEvents };
    }

    case "keydown": {
      if (!state.draft.trackingActive) return state;
      const isDeletion = action.key === "Backspace" || action.key === "Delete";
      const draft = state.draft;
      return {
        ...state,
        draft: {
          ...draft,
          metrics: {
            ...draft.metrics,
            keystrokeCount: draft.metrics.keystrokeCount + 1,
            deletionCount: draft.metrics.deletionCount + (isDeletion ? 1 : 0),
          },
          activity: markActivity(draft, action.now, true),
        },
      };
    }

    case "paste": {
      if (!state.draft.trackingActive) return state;
      const draft = state.draft;
      const next: Draft = {
        ...draft,
        metrics: {
          ...draft.metrics,
          pasteEvents: draft.metrics.pasteEvents + 1,
          pastedCharacters: draft.metrics.pastedCharacters + action.characters,
          pastedWords: draft.metrics.pastedWords + action.words,
        },
        activity: markActivity(draft, action.now, true),
      };
      const { events, pendingEvents } = logEvent(next, state.pendingEvents, "paste", action.now, {
        estimatedChars: action.characters,
      });
      return { ...state, draft: { ...next, events }, pendingEvents };
    }

    case "editorFocus": {
      if (!state.draft.trackingActive) return state;
      const draft = state.draft;
      const next: Draft = {
        ...draft,
        metrics: { ...draft.metrics, focusEvents: draft.metrics.focusEvents + 1 },
        activity: { ...markActivity(draft, action.now, false), isFocused: true },
      };
      const { events, pendingEvents } = logEvent(next, state.pendingEvents, "focus", action.now);
      return { ...state, draft: { ...next, events }, pendingEvents };
    }

    case "editorBlur": {
      if (!state.draft.trackingActive) return state;
      const draft = state.draft;
      const next: Draft = {
        ...draft,
        metrics: {
          ...draft.metrics,
          blurEvents: draft.metrics.blurEvents + 1,
          sessionEndTime: action.now,
        },
        activity: { ...draft.activity, isFocused: false },
      };
      const { events, pendingEvents } = logEvent(next, state.pendingEvents, "blur", action.now);
      return { ...state, draft: { ...next, events }, pendingEvents };
    }

    case "tick": {
      if (!state.draft.trackingActive) return state;
      const draft = state.draft;
      const lastInput = draft.activity.lastInputAt
        ? new Date(draft.activity.lastInputAt).getTime()
        : 0;
      const recentlyActive = lastInput > 0 && action.now - lastInput <= IDLE_THRESHOLD_MS;
      const isActive = draft.activity.isFocused && recentlyActive;

      return {
        ...state,
        draft: {
          ...draft,
          metrics: {
            ...draft.metrics,
            totalElapsedMs: draft.metrics.totalElapsedMs + SESSION_TICK_MS,
            activeWritingMs: draft.metrics.activeWritingMs + (isActive ? SESSION_TICK_MS : 0),
            idleMs: draft.metrics.idleMs + (isActive ? 0 : SESSION_TICK_MS),
          },
        },
      };
    }

    case "sessionsUpdated":
      return { ...state, sessions: action.sessions };

    case "sessionSelected":
      return { ...state, selectedSessionId: action.id };

    case "sessionLoaded": {
      // A loaded session restores the words into the editor with a fresh
      // evidence set: same reset semantics as deleting the open draft, but the
      // emails stay put. When the snapshot carried metrics they come back with
      // it, so the summary resumes where the save happened; a legacy session
      // saved without metrics falls back to the zeroed fresh draft.
      // When the snapshot carries HTML, the text is re-derived from that markup
      // through the shared paragraph-preserving converter, so the two always
      // agree on structure. A compact session (saved when storage was full)
      // carries no HTML at all: its stored text is the only content source, and
      // the HTML is rebuilt from it so the editor renders the restored words
      // instead of the empty-state hint.
      const restoredHtml = action.session.html
        ? action.session.html
        : draftHtmlFromStructuredText(action.session.text);
      const restored = createDraft(restoredHtml);
      const restoredText = action.session.html
        ? htmlToStructuredText(action.session.html)
        : action.session.text;
      // A stored session may carry metrics with no majorGrowthEvents array at
      // all (isDraftSession deliberately admits loosely-shaped entries), so the
      // legacy cap below must not assume the list exists.
      const mge = action.session.metrics?.majorGrowthEvents;
      const metrics = action.session.metrics
        ? {
            ...action.session.metrics,
            wordCount: action.session.wordCount,
            // A legacy session saved by a pre-round-8 build can carry an
            // oversized majorGrowthEvents list. The API schema rejects anything
            // past the cap, so capping on restore (newest kept, matching the
            // append path) is what stops every syncDraft PUT from 400ing and
            // leaving the stale content stuck as the last save.
            majorGrowthEvents: Array.isArray(mge) ? mge.slice(-MAX_MAJOR_GROWTH_EVENTS) : [],
          }
        : restored.metrics;
      return {
        ...state,
        referencePrompt: action.session.referencePrompt ?? "",
        draft: {
          ...restored,
          text: restoredText,
          professorEmail: state.draft.professorEmail,
          studentEmail: state.draft.studentEmail,
          trackingActive: state.draft.trackingActive,
          metrics,
          activity: { ...restored.activity, isFocused: false },
        },
        contentVersion: state.contentVersion + 1,
        saveStatus: "Not saved",
        pendingEvents: [],
        guidance: null,
        selectedSessionId: action.session.id,
      };
    }

    case "draftCleared":
      // The last saved session is gone: fall back to a brand-new editor while
      // keeping the emails and the tracking state untouched.
      {
        const fresh = createDraft("");
        return {
          ...state,
          referencePrompt: "",
          draft: {
            ...fresh,
            professorEmail: state.draft.professorEmail,
            studentEmail: state.draft.studentEmail,
            trackingActive: state.draft.trackingActive,
            activity: { ...fresh.activity, isFocused: false },
          },
          contentVersion: state.contentVersion + 1,
          saveStatus: "Not saved",
          pendingEvents: [],
          selectedSessionId: null,
          guidance: "Saved sessions cleared. The editor is back to a fresh draft.",
        };
      }

    case "saveStatus":
      return {
        ...state,
        saveStatus: action.value,
      };

    case "submitted":
      return {
        ...state,
        // Submitting resets everything, including the assignment notes.
        referencePrompt: "",
        draft: {
          ...action.draft,
          // The status bar shows the confirmation number; the student's PDF
          // copy is delivered automatically by the provider on submit.
          currentSubmissionId: action.submission.submissionId,
          activity: { ...action.draft.activity, isFocused: false },
        },
        contentVersion: state.contentVersion + 1,
        saveStatus: "Not saved",
        pendingEvents: [],
        selectedSessionId: null,
        guidance: `Submission ${action.submission.submissionId} is ready. Tracking has stopped, and your writing space has been reset for a new draft.`,
      };

    case "guidance":
      return { ...state, guidance: action.message };

    case "eventsFlushed":
      // The provider passes the identities of the events that were actually
      // delivered to the service, and the reducer removes EXACTLY those from
      // the authoritative live buffer. No count/slice indirection: a front
      // slice could over-remove when the cap trimmed already-sent events or a
      // dispatched-but-unrendered mutation sits at the newest end, which the
      // provider's post-render snapshot would not reflect.
      {
        const sentSet = new Set(action.sent);
        return { ...state, pendingEvents: state.pendingEvents.filter((event) => !sentSet.has(event)) };
      }

    case "eventsQueued":
      // Events that could not be delivered before a draft-resetting action
      // (e.g. the post-submit flush failing) are re-queued here so they are
      // never silently dropped with the buffer wipe; the cap still applies.
      return {
        ...state,
        pendingEvents: [...state.pendingEvents, ...action.events].slice(-MAX_PENDING_EVENTS),
      };

    default:
      return state;
  }
}
